import type { ComponentChild } from "preact";
import type {
  ConvData,
  ConflictReason,
  LeadData,
} from "../../types";
import { fmtCanonicalPt, fmtCanonicalPtItems, fmtCurrencyBRL, fmtDateTime } from "../../format";
import { Card } from "../atoms/Card";
import { KV } from "../atoms/KV";
import type { KVPair } from "../atoms/KV";
import { Chips } from "../atoms/Chip";
import { StateBadge, PriorityBadge } from "../atoms/Badge";
import { PhoneLink } from "../atoms/PhoneLink";
import { ChannelBadge } from "../atoms/ChannelBadge";
import { crmFinalLabel, EgoLink, getCrmFinalTarget } from "../CrmLinks";
import { NegativeReplyAction } from "../NegativeReplyAction";
import type { NegativeReplyActionProps } from "../NegativeReplyAction";

const CONFLICT_REASON_LABEL: Record<ConflictReason, string> = {
  tier_1_workaround_send_media: "Tier-1 · pediu fotos/áudio antes de fechar funil",
  tier_1_out_of_scope: "Tier-1 · saiu do escopo (assunto não-imóvel)",
  tier_2_vague_cumulative: "Tier-2 · respostas vagas acumuladas",
  mandatory_min_unreached: "Sem mínimo viável para acionar W4",
};

const KNOWN_LEAD_FIELDS = new Set([
  "intent",
  "neighborhoods",
  "bairros",
  "budget",
  "budget_currency",
  "budget_brl_numeric",
  "orcamento_brl_numeric",
  "orcamento_brl_raw",
  "orcamento_raw",
  "bedrooms",
  "quartos",
  "requirements",
  "requirements_raw",
  "requisitos",
  "requisitos_raw",
  "property_type",
  "tipo_imovel",
  "dates",
  "datas_estadia",
  "guests",
  "group_composition",
  "size_sqm",
  "listing_service",
  "move_in_date",
  "timeline",
  "stay_too_short",
  "name",
  "nome",
  "email",
  "phone",
  "telefone",
  "expected_price",
  "expected_monthly_rent",
  "expected_daily_rate",
  "guests_capacity",
  "appraisal_purpose",
  "management_scope",
  "preco_esperado_brl_numeric",
  "preco_esperado_brl_raw",
]);

function buildExtras(ld: LeadData): ComponentChild | null {
  const entries = Object.entries(ld).filter(([k, v]) =>
    !k.startsWith("_") && !KNOWN_LEAD_FIELDS.has(k) && v != null && v !== ""
  );
  if (entries.length === 0) return null;
  return (
    <>
      {entries.map(([k, v]) => (
        <div key={k}>
          <b>{k}:</b> {Array.isArray(v) ? v.join(", ") : String(v)}
        </div>
      ))}
    </>
  );
}

function stringValue(v: unknown): string | null {
  if (v == null || v === "") return null;
  if (typeof v === "string") return v.trim() || null;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return null;
}

function firstString(...values: unknown[]): string | null {
  for (const v of values) {
    const s = stringValue(v);
    if (s) return s;
  }
  return null;
}

function firstArrayOrString(...values: Array<string[] | string | undefined | null>): string[] | string | null {
  for (const v of values) {
    if (Array.isArray(v) && v.length > 0) return v;
    if (typeof v === "string" && v.trim()) return v;
  }
  return null;
}

function budgetDisplay(ld: LeadData): string | null {
  const raw = firstString(ld.budget, ld.orcamento_brl_raw, ld.orcamento_raw);
  const numeric = ld.budget_brl_numeric ?? ld.orcamento_brl_numeric;
  const num = fmtCurrencyBRL(numeric);
  if (num && raw) return `${num} (${raw})`;
  return num || raw || null;
}

export function ResumoTab({ data, negativeReply }: { data: ConvData; negativeReply: Omit<NegativeReplyActionProps, "placement"> }) {
  const { conv, session, lead_data, conflict_payload, messages } = data;
  const ld: LeadData = lead_data || {};

  const source = session?.source ?? "whatsapp";
  const leadEmail = ld.email || null;
  const displayEmail = session?.user_email || leadEmail;
  const leadPairs: KVPair[] = [
    ["Nome", session?.user_name ?? null],
    ["Fonte", <ChannelBadge source={source} />],
    [
      "Telefone",
      <span class="phone-line">
        <PhoneLink phone={session?.user_phone} source={source} />
      </span>,
    ],
    ["E-mail", displayEmail],
    ["Idioma", conv?.language ?? null],
    ["Prioridade", <PriorityBadge priority={conv?.lead_priority} />],
  ];

  const propertyType = firstString(ld.property_type, ld.tipo_imovel);
  const neighborhoods = firstArrayOrString(ld.neighborhoods, ld.bairros);
  const bedrooms = firstString(ld.bedrooms, ld.quartos);
  const requirements = firstArrayOrString(ld.requirements, ld.requisitos);
  const requirementsRaw = firstArrayOrString(ld.requirements_raw, ld.requisitos_raw);
  const dates = firstString(ld.dates, ld.datas_estadia);

  const funnelPairs: KVPair[] = [
    ["Intent", fmtCanonicalPt(ld.intent || conv?.intent || null)],
    ["Serviço", fmtCanonicalPt(ld.listing_service)],
    ["Tipo imóvel", fmtCanonicalPt(propertyType)],
    ["Bairros", <Chips items={neighborhoods} />],
    ["Orçamento", budgetDisplay(ld)],
    ["Metragem", ld.size_sqm ? `${ld.size_sqm} m²` : null],
    ["Quartos", bedrooms],
    ["Hóspedes", firstString(ld.guests)],
    ["Capacidade", firstString(ld.guests_capacity)],
    ["Grupo", fmtCanonicalPt(ld.group_composition)],
    ["Requisitos", <Chips items={fmtCanonicalPtItems(requirements)} />],
    ["Requisitos raw", <Chips items={requirementsRaw} />],
    ["Datas estadia", dates],
    ["Entrada", ld.move_in_date ?? null],
    ["Timeline", ld.timeline ?? null],
    ["Finalidade avaliação", ld.appraisal_purpose ?? null],
    ["Escopo gestão", ld.management_scope ?? null],
  ];
  const extras = buildExtras(ld);
  if (extras) funnelPairs.push(["Outros", extras]);

  const turnCount = Array.isArray(messages)
    ? messages.filter((m) => m.role === "user").length
    : 0;

  const cp = conflict_payload;
  const conflictExists = !!(cp && Object.keys(cp).length > 0);
  const countersStr = cp?.counters_at_trigger
    ? Object.entries(cp.counters_at_trigger)
      .map(([k, v]) => `${k}=${v}`)
      .join(" · ")
    : null;

  const conversationPairs: KVPair[] = [
    ["Estado terminal", <StateBadge state={conv?.terminal_state} />],
    ["Close reason", conv?.close_reason ?? null],
    [
      "Entity eGO",
      <EgoLink
        id={session?.ego_entity_id}
        label="Entity"
        kind="person"
      />,
    ],
    ["Tipo CRM final", conv?.crm_final_emission_kind ? crmFinalLabel(conv.crm_final_emission_kind, true) : null],
    [
      crmFinalLabel(conv?.crm_final_emission_kind),
      (() => {
        const target = getCrmFinalTarget(
          conv?.crm_final_emission_kind,
          conv?.ego_opportunity_id,
          conv?.ego_lead_id,
          conv?.ego_opportunity_url,
          conv?.ego_lead_url,
        );
        return <EgoLink id={target.id} label={target.label} kind="lead" url={target.url} />;
      })(),
    ],
    ["Lead enviado", fmtDateTime(conv?.lead_sent_at)],
    ["Erro W5", conv?.w5_error ?? null],
    ["Criada em", fmtDateTime(conv?.created_at)],
    ["Última atividade", fmtDateTime(conv?.updated_at)],
    ["Turnos", turnCount],
  ];

  return (
    <div class="summary-layout">
      <div class="summary-main">
        <Card title="Lead">
          <KV pairs={leadPairs} />
        </Card>

        <Card title="Funil">
          <KV pairs={funnelPairs} filterEmpty />
        </Card>

        {conflictExists && cp && (
          <Card title="⚠ Conflict handoff" variant="danger">
            <KV
              pairs={[
                [
                  "Motivo",
                  cp.reason
                    ? CONFLICT_REASON_LABEL[cp.reason as ConflictReason] ??
                      cp.reason
                    : null,
                ],
                ["Tier", cp.tier ?? null],
                ["Disparado em", fmtDateTime(conv?.conflict_escalated_at)],
                ["Contadores", countersStr],
                ["Última msg útil", fmtDateTime(conv?.last_useful_msg_at)],
              ]}
            />
          </Card>
        )}

        <Card title="Conversa">
          <KV pairs={conversationPairs} />
        </Card>
      </div>
      <NegativeReplyAction {...negativeReply} placement="summary" />
    </div>
  );
}
